import React, { useMemo, useCallback } from 'react';
import { useLocale } from '@edreamsodigeo/frontend-core';
import { FormSelector } from '@/components/FormField';
import { useCountries } from '@/hooks/GQL';
import { Spinner, Box } from '@edreamsodigeo/prisma-design-system';
import PropTypes from 'prop-types';

const CountrySelector = ({ onBlur, value, ...props }) => {
  const locale = useLocale();
  const { data, loading } = useCountries({ locale });
  const { getCountries: { countries } = {} } = data || {};

  const values = useMemo(() => {
    if (countries) {
      return countries.map(({ phonePrefix, name, id }) => [
        `${name} (${phonePrefix})`,
        `${id}`,
      ]);
    }
    return [];
  }, [countries]);

  const defaultValue = useMemo(() => {
    if (countries) {
      const id = getBrowserDefaultCountry(countries)?.id;
      return `${id ?? ''}`;
    }
    return '';
  }, [countries]);

  // handle onBlur to return phonePrefix instead of country id
  const onBlurInternal = useCallback(
    (val) => {
      if (onBlur) {
        const country = countries.find((country) => country.id === +val);
        onBlur(country?.phonePrefix ?? '');
      }
    },
    [countries, onBlur]
  );

  // derive internal value from external phonePrefix value
  const internalValue = useMemo(() => {
    const country = countries?.find((country) => country.phonePrefix === value);
    return `${country?.id ?? ''}`;
  }, [countries, value]);

  return (
    <div data-testid="country-selector">
      {loading ? (
        <Box position="relative">
          <FormSelector values={[]} {...props} />
          <Box
            position="absolute"
            sx={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <Spinner
              position="absolute"
              size="medium"
              data-testid="country-selector-spinner"
            />
          </Box>
        </Box>
      ) : (
        <FormSelector
          values={values || []}
          disabled={!values || values?.length === 0}
          defaultValue={defaultValue}
          {...props}
          onBlur={onBlurInternal}
          value={internalValue}
        />
      )}
    </div>
  );
};

export function getBrowserDefaultCountry(countries = []) {
  if (navigator && (navigator.language || navigator.userLanguage)) {
    const locale = navigator.language
      .split('-')
      .slice(1)
      .map((item) => item.toUpperCase());

    return countries.find((country) =>
      locale.includes(country.code.toUpperCase())
    );
  }
}

CountrySelector.propTypes = {
  onBlur: PropTypes.func,
  value: PropTypes.string,
};

export { CountrySelector };
